// Interruptor "formato nuevo" del presupuesto (en pruebas).
// Mientras está desactivado, la página del presupuesto, el PDF y el Dashboard
// se ven exactamente como siempre.
//
// Se guarda POR USUARIO en el servidor (GET/PUT /api/preferencias, clave
// `presupuesto_formato_nuevo`), así sigue a la persona en todos sus
// dispositivos. Funcionamiento:
// - Almacén a nivel de módulo (valor + suscriptores): todas las pantallas que
//   usan el hook (ficha de presupuesto, Dashboard) comparten el mismo estado.
// - Valor inicial síncrono desde localStorage (espejo local) para no parpadear
//   mientras llega la respuesta del servidor.
// - La lectura del servidor se hace una sola vez por sesión de página y por
//   usuario (promesa cacheada por token; si cambia el token, se vuelve a leer).
//   Si el servidor devuelve true/false, manda y se copia en localStorage. Si
//   devuelve null (el usuario nunca lo ha guardado) y este navegador lo tenía
//   activado ('1'), se hace un único PUT con true para migrarlo al usuario.
// - setActivo(valor) cambia el almacén y localStorage al instante y guarda en
//   el servidor (en orden); si falla, se queda el valor local y solo se avisa
//   con console.warn.
// - Sin sesión o con el servidor caído, funciona solo con localStorage.
// - Se sincroniza entre pestañas del navegador con el evento `storage`.
import { useEffect, useState } from 'react';
import { getAuthToken, preferenciasAPI } from '../api/api';

const CLAVE = 'presupuesto_formato_nuevo';

const leerLocal = () => {
  try {
    return window.localStorage.getItem(CLAVE) === '1';
  } catch (e) {
    return false;
  }
};

const escribirLocal = (valor) => {
  try {
    if (valor) window.localStorage.setItem(CLAVE, '1');
    else window.localStorage.removeItem(CLAVE);
  } catch (e) {
    // Sin acceso a localStorage (modo privado, bloqueado...): solo en memoria
  }
};

const leerToken = () => {
  try {
    return getAuthToken() || null;
  } catch (e) {
    return null;
  }
};

const almacen = {
  valor: leerLocal(),
  suscriptores: new Set(),
  // Lectura del servidor en curso o hecha, y el token con el que se hizo
  carga: null,
  tokenCarga: null,
  // Sube con cada cambio del usuario: una lectura que llega tarde no lo pisa
  version: 0,
  // Guardados en el servidor encadenados para que lleguen en orden
  cola: Promise.resolve(),
};

const publicar = (valor) => {
  almacen.valor = valor;
  almacen.suscriptores.forEach((fn) => fn(valor));
};

const guardarEnServidor = (valor) => {
  if (!leerToken()) return;
  almacen.cola = almacen.cola
    .then(() => preferenciasAPI.guardar({ [CLAVE]: valor }))
    .catch((e) => {
      console.warn('No se ha podido guardar el formato nuevo en el servidor; se mantiene en este navegador.', e);
    });
};

const cargarDelServidor = () => {
  const token = leerToken();
  if (!token) return;
  if (almacen.carga && almacen.tokenCarga === token) return;
  almacen.tokenCarga = token;
  const versionAlEmpezar = almacen.version;
  almacen.carga = preferenciasAPI
    .obtener()
    .then((res) => {
      // Cambio de usuario o cambio hecho mientras llegaba la respuesta: no tocar
      if (almacen.tokenCarga !== token || almacen.version !== versionAlEmpezar) return;
      const delServidor = res && res.data ? res.data[CLAVE] : undefined;
      if (delServidor === true || delServidor === false) {
        escribirLocal(delServidor);
        publicar(delServidor);
      } else if (delServidor === null && leerLocal()) {
        // Nunca guardado en el servidor: migrar lo que tenía este navegador
        guardarEnServidor(true);
      }
    })
    .catch((e) => {
      console.warn('No se ha podido leer el formato nuevo del servidor; se usa el de este navegador.', e);
    });
};

const setFormatoNuevo = (valor) => {
  const nuevo = !!valor;
  almacen.version += 1;
  escribirLocal(nuevo);
  publicar(nuevo);
  guardarEnServidor(nuevo);
};

const useFormatoNuevo = () => {
  const [activo, setActivoEstado] = useState(() => almacen.valor);

  useEffect(() => {
    almacen.suscriptores.add(setActivoEstado);
    // Por si cambió entre el primer render y este efecto
    setActivoEstado(almacen.valor);
    cargarDelServidor();

    const alCambiarEnOtraPestana = (e) => {
      if (e.key === null || e.key === CLAVE) {
        // La otra pestaña ya lo ha guardado en el servidor
        almacen.version += 1;
        publicar(leerLocal());
      }
    };
    window.addEventListener('storage', alCambiarEnOtraPestana);
    return () => {
      almacen.suscriptores.delete(setActivoEstado);
      window.removeEventListener('storage', alCambiarEnOtraPestana);
    };
  }, []);

  return [activo, setFormatoNuevo];
};

export default useFormatoNuevo;
